import { useContext, useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import { toast } from 'sonner';

import {
    ChainlitContext,
    type IThread,
    threadHistoryState
} from '@chainlit/react-client';

interface UseThreadRenameOptions {
    threadId: string | undefined;
    thread: IThread | undefined;
    historyThread: IThread | undefined;
    mutateApiThread: () => void;
}

export function useThreadRename({
    threadId,
    thread,
    historyThread,
    mutateApiThread
}: UseThreadRenameOptions) {
    const apiClient = useContext(ChainlitContext);
    const [threadHistory, setThreadHistory] =
        useRecoilState(threadHistoryState);

    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState('');
    const [localName, setLocalName] = useState<string | undefined>();
    const inputRef = useRef<HTMLInputElement>(null);

    const threadName = localName ?? thread?.name;

    // Sync localName when thread data changes
    useEffect(() => {
        setLocalName(undefined);
    }, [thread?.name]);

    // Focus and select text when entering edit mode
    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    const startEditing = () => {
        setEditValue(threadName || '');
        setIsEditing(true);
    };

    const saveRename = async () => {
        const trimmed = editValue.trim();
        setIsEditing(false);

        if (!threadId || !trimmed || trimmed === threadName) return;

        try {
            await apiClient.renameThread(threadId, trimmed);
            setLocalName(trimmed);
            setThreadHistory((prev) => {
                const threads = prev?.threads ? [...prev.threads] : [];
                const idx = threads.findIndex((t) => t.id === threadId);
                if (idx !== -1) {
                    threads[idx] = { ...threads[idx], name: trimmed };
                }
                return { ...prev, threads };
            });
            if (!historyThread) {
                mutateApiThread();
            }
        } catch (err) {
            console.error('Failed to rename thread:', err);
            toast.error('Failed to rename thread');
            setLocalName(undefined);
        }
    };

    const cancelEditing = () => {
        setIsEditing(false);
        setEditValue('');
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            saveRename();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelEditing();
        }
    };

    const renameThread = async (newName: string) => {
        const trimmed = newName.trim();

        if (!threadId || !trimmed || trimmed === threadName) return;

        try {
            await apiClient.renameThread(threadId, trimmed);
            setLocalName(trimmed);
            setThreadHistory((prev) => {
                const threads = prev?.threads ? [...prev.threads] : [];
                const idx = threads.findIndex((t) => t.id === threadId);
                if (idx !== -1) {
                    threads[idx] = { ...threads[idx], name: trimmed };
                }
                return { ...prev, threads };
            });
            if (!historyThread) {
                mutateApiThread();
            }
        } catch (err) {
            console.error('Failed to rename thread:', err);
            toast.error('Failed to rename thread');
            setLocalName(undefined);
            throw err;
        }
    };

    return {
        isEditing,
        editValue,
        setEditValue,
        inputRef,
        threadName,
        startEditing,
        saveRename,
        handleKeyDown,
        renameThread
    };
}
